import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HANDOVER_RIGHTS_LABEL, SETUP_STATUS_LABEL } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { dirClass, dirLabel, fmtCountdown, fmtTime, titleCase, useNow } from '../lib/format';
import type { SetupDetail as Detail, SetupStatus } from '../lib/types-setups';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Textarea } from '../components/ui';
import { DiscussLive } from './Rooms';
import { NextUpdate, SETUP_TONE, SetupStatusPill, zoneText } from './Setups';

const UPDATE_STATUSES: { value: SetupStatus; label: string }[] = [
  { value: 'WATCHING', label: 'Watching' },
  { value: 'GETTING_READY', label: 'Getting ready' },
  { value: 'TRIGGERED', label: 'Triggered' },
  { value: 'INVALIDATED', label: 'Invalidated' },
];
const STATUS_COLORS: Partial<Record<SetupStatus, string>> = { WATCHING: '#c9d0da', GETTING_READY: '#f5b544', TRIGGERED: '#3dd68c', INVALIDATED: '#ff7a6e' };
const CONFIDENCE = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-[#1a1f27] py-2.5 first:border-t-0 first:pt-0">
      <span className="text-xs text-muted">{k}</span>
      <span className="leading-relaxed text-ink">{children}</span>
    </div>
  );
}

function UpdateForm({ s, forOwner, onDone }: { s: Detail; forOwner: string | null; onDone: (msg: string) => void }) {
  const [status, setStatus] = useState<SetupStatus>(s.status);
  const [whatChanged, setWhatChanged] = useState('');
  const [distance, setDistance] = useState('');
  const [confidence, setConfidence] = useState<string>(s.confidence ?? 'MEDIUM');
  const [link, setLink] = useState('');
  const post = useMutation({
    mutationFn: () => api<Detail>(`/setups/${s.id}/updates`, { body: { status, whatChanged, distanceNote: distance || null, confidence, screenshotUrl: link } }),
    onSuccess: (d) => {
      setWhatChanged('');
      setDistance('');
      setLink('');
      onDone(d.status === 'INVALIDATED' ? 'Setup invalidated and closed. It stays searchable.' : `Update posted to the floor${d.nextUpdate ? ` · next one due ${fmtCountdown(d.nextUpdate.dueAt, Date.now())}` : ''}.`);
    },
  });
  return (
    <Card tone={s.stale || s.nextUpdate?.status === 'OVERDUE' ? 'down' : s.nextUpdate?.status === 'DUE' ? 'warn' : undefined} className="flex flex-col gap-3.5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-base font-bold">Post an update{forOwner ? ` for ${s.owner.name}` : ''}</h2>
        <span className="text-xs text-muted">{s.cadenceLabel}</span>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted">Status</span>
        <Segmented label="Status" options={UPDATE_STATUSES} value={status} onChange={setStatus} colors={STATUS_COLORS} />
        {status === 'INVALIDATED' && <span className="text-xs text-warn">Invalidating closes the setup. It can't take more updates afterwards.</span>}
      </div>
      <Field label="What changed">
        <Textarea value={whatChanged} onChange={(e) => setWhatChanged(e.target.value)} placeholder="e.g. Price tagged the zone and printed a rejection wick on M15" />
      </Field>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Distance to trigger / invalidation" hint="Free text, e.g. 4 pips to trigger · 33 to invalidation">
          <Input value={distance} onChange={(e) => setDistance(e.target.value)} />
        </Field>
        <Field label="Updated chart link (optional)" hint="TradingView or screenshot link">
          <Input type="url" inputMode="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" />
        </Field>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted">Confidence</span>
        <Segmented label="Confidence" options={CONFIDENCE} value={confidence} onChange={setConfidence} />
      </div>
      <ErrorText error={post.error ? errorText(post.error) : null} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted">{s.stale ? 'Posting clears the Stale badge (it is recorded as late).' : 'Posting resets the update clock and appears on the floor.'}</span>
        <Button variant="primary" onClick={() => post.mutate()} disabled={whatChanged.trim().length < 3 || post.isPending}>
          {post.isPending ? 'Posting…' : 'Post update'}
        </Button>
      </div>
    </Card>
  );
}

export function SetupDetailPage() {
  const { id } = useParams();
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const now = useNow(1000);
  const tz = me.data?.timezone ?? 'UTC';
  const [msg, setMsg] = useState<string | null>(null);
  const { data: s, error } = useQuery({ queryKey: ['setup', id], queryFn: () => api<Detail>(`/setups/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['setup', id] });
    void qc.invalidateQueries({ queryKey: ['setups'] });
    void qc.invalidateQueries({ queryKey: ['today'] });
  };
  const follow = useMutation({ mutationFn: () => api(`/setups/${id}/follow`, { method: s?.following ? 'DELETE' : 'POST' }), onSuccess: refresh });

  if (error) return <ErrorText error={errorText(error)} />;
  if (!s) return null;
  const covering = s.cover?.id === me.data?.id;

  return (
    <>
      <PageHeader
        back={<Link to="/setups" className="text-[13px]">← Setups</Link>}
        title={
          <>
            {s.symbol} <span className={dirClass(s.direction)}>{dirLabel(s.direction)}</span> <span className="text-base font-normal text-muted">· {s.timeframe}</span>
          </>
        }
        sub={`Marked by ${s.owner.name} · ${fmtTime(s.createdAt, tz, true)} · ${s.market.code}${s.strategy ? ` · ${s.strategy.code} ${s.strategy.name}` : ''}`}
        right={
          <>
            <SetupStatusPill status={s.status} stale={s.stale} />
            {!s.closedAt && (s.can.edit || hasRole(me.data?.roles, 'MASTER_TRADER', 'RISK_MANAGER', 'ADMIN')) && <DiscussLive type="SETUP" id={s.id} />}
            {s.can.follow && (
              <Button onClick={() => follow.mutate()} disabled={follow.isPending} aria-pressed={s.following}>
                {s.following ? 'Following ✓' : 'Follow'}
              </Button>
            )}
            {s.can.edit && (
              <Link to={`/setups/${s.id}/edit`} className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-4 py-2.5 text-sm font-semibold text-ink no-underline hover:text-ink">
                Edit
              </Link>
            )}
            {s.can.convert && (
              <Button variant="primary" onClick={() => nav(`/plans/new?setup=${s.id}`)}>
                Convert to plan
              </Button>
            )}
          </>
        }
      />
      {msg && <p role="status" className="m-0 rounded-lg border border-[#1e5a3c] bg-up-bg px-3 py-2 text-sm text-up">{msg}</p>}
      <ErrorText error={follow.error ? errorText(follow.error) : null} />
      {s.cover && (
        <Card tone="live" className="flex flex-wrap items-center gap-2 px-4 py-3">
          <Pill tone="live">Covered</Pill>
          <span className="text-ink2">
            {covering ? `You're covering this setup for ${s.owner.name}` : `${s.cover.name} is covering this setup`} {s.cover.recurring ? `during ${s.owner.name}'s quiet hours` : `until ${fmtTime(s.cover.until, tz, true)}`} · {HANDOVER_RIGHTS_LABEL[s.cover.rights]}. Update reminders go to {covering ? 'you' : s.cover.name}.
          </span>
        </Card>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_460px] flex-col gap-3.5">
          {s.can.update && (
            <UpdateForm
              key={s.status}
              s={s}
              forOwner={covering ? s.owner.name : null}
              onDone={(m) => {
                setMsg(m);
                refresh();
              }}
            />
          )}
          <Card>
            <CardHeader title={<>Updates <span className="font-normal text-muted">· {s.updates.length}</span></>} right={<span className="text-xs text-muted">Newest first · never edited</span>} />
            {s.updates.map((u) => (
              <article key={u.id} className="flex gap-3.5 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
                <span className="min-w-[92px] font-mono text-xs text-muted">{fmtTime(u.createdAt, tz, true)}</span>
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill tone={SETUP_TONE[u.status]}>{SETUP_STATUS_LABEL[u.status]}</Pill>
                    {u.confidence && <span className="text-xs text-sub">{titleCase(u.confidence)} confidence</span>}
                  </div>
                  <span className="leading-relaxed text-ink">{u.status === 'EXPIRED' ? 'Expired automatically at the end of its window.' : u.whatChanged}</span>
                  {u.distanceNote && <span className="text-sm text-sub">{u.distanceNote}</span>}
                  <span className="text-xs text-muted">
                    {u.status === 'EXPIRED' ? 'by VTF' : `by ${u.author.name}${u.onBehalfOf ? ` for ${u.onBehalfOf.name} (covering)` : ''}`}
                    {u.screenshotUrl && (
                      <>
                        {' · '}
                        <a href={u.screenshotUrl} target="_blank" rel="noreferrer noopener">
                          chart
                        </a>
                      </>
                    )}
                  </span>
                </div>
              </article>
            ))}
            {!s.updates.length && <Empty>No updates yet. The first one is due {s.nextUpdate ? fmtCountdown(s.nextUpdate.dueAt, now) : 'soon'}.</Empty>}
          </Card>
        </section>

        <aside aria-label="Setup details" className="flex min-w-0 flex-[1_1_320px] flex-col gap-3.5">
          {!s.closedAt && (
            <Card tone={s.stale ? 'down' : undefined} className="flex flex-col gap-1.5 p-4">
              <span className="text-xs text-muted">Update clock</span>
              <span className="text-base font-semibold">
                <NextUpdate s={s} now={now} tz={tz} />
              </span>
              {s.nextUpdate && (
                <span className="text-xs text-muted">
                  Reminder at {fmtTime(s.nextUpdate.graceUntil, tz)} · marked stale at {fmtTime(s.nextUpdate.missAt, tz)}
                  {s.nextUpdate.assignee.id !== s.owner.id ? ` · goes to ${s.nextUpdate.assignee.name}` : ''}
                </span>
              )}
            </Card>
          )}
          <Card className="flex flex-col p-4">
            <Row k="Zone">
              <span className="font-mono">{zoneText(s)}</span>
            </Row>
            <Row k="Trigger">{s.triggerCondition}</Row>
            {s.signal && (
              <Row k="From signal">
                <Link to={`/floor?type=signals&symbol=${s.instrumentId}`}>
                  {s.signal.source.name} · {s.signal.strategy} · {s.signal.eventLabel.toLowerCase()}
                </Link>{' '}
                <span className="text-xs text-muted">· {fmtTime(s.signal.receivedAt, tz, true)}</span>
              </Row>
            )}
            <Row k="Invalidation">
              <span className="font-mono text-down">{s.invalidationPrice ?? '—'}</span>
            </Row>
            <Row k="Target idea">{s.targetIdea || '—'}</Row>
            <Row k="Confidence">{s.confidence ? titleCase(s.confidence) : '—'}</Row>
            <Row k="Updates">{s.cadenceLabel}</Row>
            <Row k={s.closedAt ? 'Closed' : 'Expires'}>{fmtTime(s.closedAt ?? s.expiresAt, tz, true)}</Row>
            {s.chartUrl && (
              <Row k="Chart">
                <a href={s.chartUrl} target="_blank" rel="noreferrer noopener" className="break-all">
                  {s.chartUrl.replace(/^https?:\/\//, '')}
                </a>
              </Row>
            )}
            <Row k="Followers">{s.followers ? `${s.followers} on the floor` : 'Nobody yet'}</Row>
          </Card>
          {!!s.plans.length && (
            <Card className="flex flex-col gap-2 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Trade plans from this setup</h2>
              {s.plans.map((p) => (
                <Link key={p.id} to={p.tradeId ? `/trades/${p.tradeId}` : `/plans/${p.id}`} className={cx('flex items-center justify-between rounded-lg bg-raised px-3 py-2 text-ink no-underline')}>
                  <span>{p.tradeId ? 'Trade' : 'Plan'} · {fmtTime(p.createdAt, tz, true)}</span>
                  <span className="text-xs font-semibold text-sub">{titleCase(p.status)} →</span>
                </Link>
              ))}
            </Card>
          )}
        </aside>
      </div>
    </>
  );
}
